import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supportingDocsAPI, cvAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  FileText, ChevronLeft, Upload, Trash2, Download, FolderOpen,
  AlertCircle, CheckCircle, Loader2, FileImage, FilePlus, Merge,
  X, Eye, File
} from 'lucide-react';
import { toast } from 'sonner';

const SupportingDocsPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const fileInputRef = useRef(null);
  
  const [documents, setDocuments] = useState([]);
  const [cvs, setCVs] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [merging, setMerging] = useState(false);
  
  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    document_type: 'certificate',
    description: '',
    file: null
  });
  
  // Merge modal state
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [selectedCV, setSelectedCV] = useState('');
  const [includeCVFirst, setIncludeCVFirst] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsRes, cvsRes, typesRes] = await Promise.all([
        supportingDocsAPI.list(),
        cvAPI.list(),
        supportingDocsAPI.getTypes()
      ]);
      setDocuments(docsRes.data.documents || []);
      setCVs(cvsRes.data || []);
      setDocumentTypes(typesRes.data.types || []);
      if (cvsRes.data?.length > 0) {
        setSelectedCV(cvsRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload PDF, JPG, PNG, or WEBP files.');
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB.');
        return;
      }
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('document_type', uploadData.document_type);
      formData.append('description', uploadData.description);
      
      await supportingDocsAPI.upload(formData);
      toast.success('Document uploaded successfully!');
      
      // Reset form and reload
      setUploadData({ document_type: 'certificate', description: '', file: null });
      setShowUploadForm(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await supportingDocsAPI.delete(docId);
      toast.success('Document deleted');
      setDocuments(documents.filter(d => d.id !== docId));
      setSelectedDocs(selectedDocs.filter(id => id !== docId));
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await supportingDocsAPI.download(doc.id);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleMerge = async () => {
    if (!selectedCV) {
      toast.error('Please select a CV');
      return;
    }
    if (selectedDocs.length === 0) {
      toast.error('Please select at least one document to merge');
      return;
    }
    
    setMerging(true);
    try {
      const response = await supportingDocsAPI.mergeWithCV({
        cv_id: selectedCV,
        document_ids: selectedDocs,
        include_cv_first: includeCVFirst
      });
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Complete_Application.pdf';
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Merged PDF downloaded!');
      setShowMergeModal(false);
      setSelectedDocs([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge documents');
    } finally {
      setMerging(false);
    }
  };

  const toggleDocSelection = (docId) => {
    if (selectedDocs.includes(docId)) {
      setSelectedDocs(selectedDocs.filter(id => id !== docId));
    } else {
      setSelectedDocs([...selectedDocs, docId]);
    }
  };

  const getDocTypeLabel = (type) => {
    const found = documentTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getFileIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return <FileText className="w-5 h-5 text-red-500" />;
    if (mimeType.startsWith('image/')) return <FileImage className="w-5 h-5 text-blue-500" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Subscription Required</h1>
          <p className="text-slate-600 mb-6">Subscribe to upload and manage supporting documents</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-600 px-2 sm:px-4"
            data-testid="back-to-dashboard"
          >
            <ChevronLeft className="w-5 h-5 sm:mr-1" />
            <span className="hidden sm:inline">Dashboard</span>
          </Button>
          <div className="h-6 w-px bg-slate-200 hidden sm:block"></div>
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-emerald-600" />
            <h1 className="font-semibold text-slate-900 text-sm sm:text-base">
              <span className="hidden sm:inline">Supporting Documents</span>
              <span className="sm:hidden">Documents</span>
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {documents.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setShowMergeModal(true)}
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 px-2 sm:px-4"
              data-testid="merge-docs-btn"
            >
              <Merge className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Merge with CV</span>
            </Button>
          )}
          <Button
            onClick={() => setShowUploadForm(true)}
            className="bg-emerald-600 hover:bg-emerald-700 px-2 sm:px-4"
            data-testid="upload-doc-btn"
          >
            <Upload className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Upload Document</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Intro */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 md:p-8 text-white mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <FolderOpen className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">Supporting Documents</h2>
                <p className="text-emerald-100">
                  Upload your certificates, diplomas, ID documents, and reference letters. 
                  Merge them with your CV to create a complete application package.
                </p>
              </div>
            </div>
          </div>

          {/* Upload Form Modal */}
          {showUploadForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl w-full max-w-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-lg text-slate-900">Upload Document</h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowUploadForm(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {/* File Input */}
                  <div>
                    <Label>Select File *</Label>
                    <div className="mt-1.5">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={handleFileSelect}
                        className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-50 file:text-emerald-700 file:font-medium hover:file:bg-emerald-100 cursor-pointer"
                        data-testid="file-input"
                      />
                    </div>
                    {uploadData.file && (
                      <p className="text-sm text-emerald-600 mt-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        {uploadData.file.name} ({formatFileSize(uploadData.file.size)})
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      Allowed: PDF, JPG, PNG, WEBP (max 10MB)
                    </p>
                  </div>

                  {/* Document Type */}
                  <div>
                    <Label>Document Type *</Label>
                    <Select 
                      value={uploadData.document_type} 
                      onValueChange={(value) => setUploadData({ ...uploadData, document_type: value })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="doc-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {documentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div>
                              <span className="font-medium">{type.label}</span>
                              <span className="text-slate-500 ml-2 text-sm">- {type.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <div>
                    <Label>Description (Optional)</Label>
                    <Textarea
                      value={uploadData.description}
                      onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                      placeholder="e.g., Matric Certificate - 2020"
                      className="mt-1.5"
                      rows={2}
                      data-testid="doc-description"
                    />
                  </div>

                  {/* Upload Button */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowUploadForm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleUpload}
                      disabled={!uploadData.file || uploading}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="confirm-upload-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Merge Modal */}
          {showMergeModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-lg text-slate-900">Merge Documents with CV</h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowMergeModal(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {/* CV Selection */}
                  <div>
                    <Label>Select CV *</Label>
                    <Select 
                      value={selectedCV} 
                      onValueChange={setSelectedCV}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="merge-cv-select">
                        <SelectValue placeholder="Choose a CV" />
                      </SelectTrigger>
                      <SelectContent>
                        {cvs.map((cv) => (
                          <SelectItem key={cv.id} value={cv.id}>
                            {cv.personal_details?.full_name || cv.title || 'Untitled CV'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Include CV First */}
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      id="include-cv"
                      checked={includeCVFirst}
                      onCheckedChange={setIncludeCVFirst}
                    />
                    <label htmlFor="include-cv" className="text-sm text-slate-700 cursor-pointer">
                      Include CV as first page (recommended)
                    </label>
                  </div>

                  {/* Document Selection */}
                  <div>
                    <Label>Select Documents to Merge</Label>
                    <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                      {documents.map((doc) => (
                        <div
                          key={doc.id}
                          onClick={() => toggleDocSelection(doc.id)}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            selectedDocs.includes(doc.id)
                              ? 'bg-emerald-50 border-2 border-emerald-500'
                              : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                          }`}
                          data-testid={`merge-doc-${doc.id}`}
                        >
                          <Checkbox
                            checked={selectedDocs.includes(doc.id)}
                            onCheckedChange={() => toggleDocSelection(doc.id)}
                          />
                          {getFileIcon(doc.mime_type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {doc.original_filename}
                            </p>
                            <p className="text-xs text-slate-500">
                              {getDocTypeLabel(doc.document_type)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {selectedDocs.length} document{selectedDocs.length !== 1 ? 's' : ''} selected
                    </p>
                  </div>

                  {/* Merge Button */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowMergeModal(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleMerge}
                      disabled={!selectedCV || selectedDocs.length === 0 || merging}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="confirm-merge-btn"
                    >
                      {merging ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Merging...
                        </>
                      ) : (
                        <>
                          <Merge className="w-4 h-4 mr-2" />
                          Download Merged PDF
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : documents.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">No Documents Yet</h3>
              <p className="text-slate-500 mb-6">
                Upload your certificates, diplomas, and other supporting documents.
              </p>
              <Button
                onClick={() => setShowUploadForm(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Your First Document
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200">
              <div className="p-4 border-b border-slate-100">
                <h3 className="font-semibold text-slate-900">
                  Your Documents ({documents.length}/20)
                </h3>
              </div>
              <div className="divide-y divide-slate-100">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
                    data-testid={`doc-item-${doc.id}`}
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      {getFileIcon(doc.mime_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {doc.original_filename}
                      </p>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                          {getDocTypeLabel(doc.document_type)}
                        </span>
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                      </div>
                      {doc.description && (
                        <p className="text-sm text-slate-500 mt-1 truncate">
                          {doc.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(doc)}
                        className="text-slate-600 hover:text-emerald-600"
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(doc.id)}
                        className="text-slate-600 hover:text-red-600"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tips */}
          <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Tips for Supporting Documents</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-900 mb-1">Keep It Relevant</p>
                <p className="text-sm text-slate-600">Only include documents that support your application.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-900 mb-1">Good Quality</p>
                <p className="text-sm text-slate-600">Ensure scans are clear and readable.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-slate-900 mb-1">Label Clearly</p>
                <p className="text-sm text-slate-600">Add descriptions so employers know what they're viewing.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupportingDocsPage;
